import type cytoscape from 'cytoscape';
import type { ElkNode, LayoutOptions } from 'elkjs';

export interface GeometryOptions {
  nodeDimensionsIncludeLabels?: boolean;
  elk?: Record<string, unknown>;
  nodeLayoutOptions?: (node: cytoscape.NodeSingular) => Record<string, unknown> | undefined;
}

export interface GeometryPosition { id: string; x: number; y: number }

// Resolve callbacks on the main thread and copy only ELK's scalar options. No case
// labels, node data, scratch objects or Cytoscape references cross the worker boundary.
function layoutOptions(source?: Record<string, unknown>): LayoutOptions {
  const result: LayoutOptions = {};
  for (const [key, value] of Object.entries(source ?? {})) {
    if (!(key === 'algorithm' || key === 'aspectRatio' || key.startsWith('elk.') || key.startsWith('org.eclipse.elk.'))) continue;
    if (typeof value === 'string' || typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) {
      result[key] = String(value);
    }
  }
  return result;
}

const dimension = (value: number) => Number.isFinite(value) && value > 0 ? value : 1;
const coordinate = (value: number) => Number.isFinite(value) ? value : 0;

export function buildLayoutGeometry(eles: cytoscape.Collection, options: GeometryOptions) {
  const graph: ElkNode = { id: 'g', children: [], edges: [], layoutOptions: layoutOptions(options.elk) };
  const nodes = eles.nodes().union(eles.nodes().ancestors());
  const byNodeId = new Map<string, ElkNode>();
  const leaves = new Map<string, cytoscape.NodeSingular>();
  nodes.forEach((node, index) => {
    const geometry: ElkNode = { id: `n${index}`, layoutOptions: layoutOptions(options.nodeLayoutOptions?.(node)) };
    if (node.isParent()) {
      geometry.children = [];
    } else {
      const dims = node.layoutDimensions({ nodeDimensionsIncludeLabels: options.nodeDimensionsIncludeLabels });
      geometry.width = dimension(dims.w);
      geometry.height = dimension(dims.h);
      geometry.x = coordinate(node.position('x')) - geometry.width / 2;
      geometry.y = coordinate(node.position('y')) - geometry.height / 2;
      leaves.set(geometry.id, node);
    }
    byNodeId.set(node.id(), geometry);
  });
  nodes.forEach((node) => {
    const geometry = byNodeId.get(node.id())!;
    const parentId = node.parent()[0]?.id();
    const parent = parentId ? byNodeId.get(parentId) : undefined;
    (parent?.children ?? graph.children)!.push(geometry);
  });
  // Parallel transactions influence the same endpoints. ELK needs one geometric
  // constraint per directed pair; every real edge remains in Cytoscape unchanged.
  const pairs = new Set<string>();
  eles.edges().forEach((edge) => {
    const source = byNodeId.get(edge.source().id());
    const target = byNodeId.get(edge.target().id());
    if (!source || !target) return;
    const pair = `${source.id}:${target.id}`;
    if (pairs.has(pair)) return;
    pairs.add(pair);
    graph.edges!.push({ id: `e${graph.edges!.length}`, sources: [source.id], targets: [target.id] });
  });
  return { graph, leaves };
}

/** ELK uses parent-relative top-left corners; Cytoscape uses absolute leaf centres. */
export function geometryPositions(graph: ElkNode): GeometryPosition[] {
  const positions: GeometryPosition[] = [];
  const visit = (node: ElkNode, parentX: number, parentY: number) => {
    const x = parentX + (node.x ?? 0);
    const y = parentY + (node.y ?? 0);
    if (node.children) {
      node.children.forEach((child) => visit(child, x, y));
    } else {
      const position = { id: node.id, x: x + (node.width ?? 0) / 2, y: y + (node.height ?? 0) / 2 };
      if (!Number.isFinite(position.x) || !Number.isFinite(position.y)) throw new Error('Invalid layout geometry');
      positions.push(position);
    }
  };
  graph.children?.forEach((node) => visit(node, 0, 0));
  return positions;
}
